import React, { useState } from 'react';
import { Card, Button, Typography, Tag, Modal, Space, Input, Badge } from 'antd';
import { 
    FireOutlined, 
    HeartOutlined, 
    CheckCircleOutlined, 
    InfoCircleOutlined,
    ExportOutlined,
    ThunderboltOutlined,
    SafetyCertificateOutlined,
    EnvironmentOutlined,
    SearchOutlined,
    ShopOutlined,
    InstagramOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

export const TONUS_LOCATIONS = {
    all: {
        id: "all",
        name: "Բոլորը",
        nameRu: "Все",
        nameEn: "All Stores",
        icon: "🌟"
    },
    zovq: {
        id: "zovq",
        name: "Zovq (Զովք)",
        nameRu: "Zovq (Зовк)",
        nameEn: "Zovq Supermarket",
        icon: "🛒",
        badgeColor: "#2e7d32",
        addresses: [
            "Հ. Հակոբյան 8/2", "Բագրատունյաց 12", "Փափազյան 14", "Գյուրջյան 11/3", 
            "Դրո 36", "Բակունց 1", "Սարյան 12/13", "Մուրացան 113/4", 
            "Դավիթ Բեկ 7/3", "Սեբաստիա 1", "Բեկնազարյան 3/3", "Կոմիտաս 56", 
            "Զվարթնոց", "Շերամ 77", "Շիրազ 38", "Արշակունյաց 54/1", 
            "Խորենացի 33/7", "Ազատության 11", "Գյուլբենկյան 19", "Հանրապետության 71", 
            "Նալբանդյան 102", "Գալշոյան 4/41", "Աբովյան 48", "Վիլնյուս 85", 
            "Ամիրյան 15", "Ռայնիսի 6/3", "Բաբաջանյան 52 շենք 140խ", "Դավթաշեն 4-րդ թաղ. 26/1", 
            "Ավան Առինջ 2 միկրո 2/22", "Վերին պտղնի 1-ին նոր թաղ. 13", "Փարաքար, Երևանյան փ. 26", 
            "Չարենցավան, Ա. Խանջյան փ. 12", "Լենինգրադյան և Լենինական խաչմերուկ", "Աբովյան 1/3", 
            "Ավանեսով 7", "Նար Դոս 21"
        ]
    },
    sas: {
        id: "sas",
        name: "SAS",
        nameRu: "SAS",
        nameEn: "SAS Supermarket",
        icon: "🛍️",
        badgeColor: "#d32f2f",
        addresses: [
            "Կոմիտաս 52", "Իսահակյան 35", "Քաջազնունի 20/1", "Մաշտոցի 18", 
            "Մաշտոցի 15", "Արշակունյաց 11/7", "Կիևյան 1ա թիվ 39", "Հալաբյան 22/5", 
            "Թումանյան 31", "Բաղրամյան 85"
        ]
    },
    mg: {
        id: "mg",
        name: "MG Group",
        nameRu: "MG Group",
        nameEn: "MG Group",
        icon: "🏪",
        badgeColor: "#c2185b",
        addresses: [
            "Մուրացան 113 շ.", "Էրեբունի 30 շ., 49 շինություն", "Միկոյան 27/44", "Շիրակի փ. 17 շենք, 41", 
            "Հ. Ավետիսյան 32", "Բագրատունյաց 33 34", "Բաբաջանյան 147 շ. 1/1", "Շինարարների 28/2 շենք 73", 
            "Արշակունյաց 42 շենք 55 շինություն", "Արզումանյան 10", "Փափազյան 26", "Նոր Արեշի 15 փ., 14 տուն", 
            "Դավթաշեն 3-րդ թաղ. 20 շ. 54 տ-ծք", "Սասնա Ծռեր 4", "Ավան, Դուրյան 39 շ., թիվ 53 խանութ", 
            "Մոլդովական 72 13/1", "Հ. Ներսիսյան փ. փակուղի 1 շենք, 107 տարածք", "Շերամ 111/6", 
            "Արցախի պողոտա 6/38", "Շիրակի 2/24", "Ա. Բակունցի 13/4", "Սեբաստիայի փ. 24 շ. 50", 
            "Վ. Պտղնի 2/1", "Ադոնց 2/4"
        ]
    },
    gurmenia: {
        id: "gurmenia",
        name: "Gurmenia",
        nameRu: "Gurmenia",
        nameEn: "Gurmenia",
        icon: "🥑",
        badgeColor: "#ed6c02",
        addresses: [
            "Աբովյան 17", "Թումանյան 5ա"
        ]
    },
    regions: {
        id: "regions",
        name: "Մարզեր",
        nameRu: "Регионы",
        nameEn: "Regions",
        icon: "📍",
        badgeColor: "#0288d1",
        addresses: [
            "ք. Վանաձոր, Լազյան 33 («Վան Բուլանժե»)",
            "ք. Գյումրի, Խրիմյան Հայրիկ 55/1 («Մագնատ» խանութ)",
            "ք. Գյումրի, Դուդկոյի 2 և Ռիժկովի 1 («Գուրման Մարկետ» խանութներ)",
            "ք. Դիլիջան, Կալինինի 237/5 («Իննա» խանութ)"
        ]
    }
};

const INSTAGRAM_URL = "https://www.instagram.com/tonus.bread/";

const TonusBanner = () => {
    const { i18n } = useTranslation();
    const lang = i18n.language || 'hy';
    const isRu = lang === 'ru';
    const isEn = lang === 'en';

    const [activeTab, setActiveTab] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLocationsOpen, setIsLocationsOpen] = useState(false);
    const [selectedChain, setSelectedChain] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const content = {
        hy: {
            title: "«ՏՈՆՈՒՍ» — Առանց Ալյուրի և Խմորիչի Առողջարար Հաց 🌾",
            subtitle: "Պատրաստված է 100% ծլեցված հատիկներից Հայաստանում: Պահպանում է հացահատիկի բոլոր վիտամիններն ու օգտակար հանքանյութերը:",
            badges: ["0% Ալյուր & Խմորիչ", "100% Ծլեցված Հատիկ", "Ցածր Գլիկեմիկ Ինդեքս"],
            tabs: [
                {
                    icon: <SafetyCertificateOutlined />,
                    label: "0% Ալյուր & Խմորիչ",
                    detail: "Պատրաստվում է ոչ թե ալյուրից, այլ հատուկ տեխնոլոգիայով ծլեցված ամբողջական ցորենի հատիկներից:"
                },
                {
                    icon: <HeartOutlined />,
                    label: "Առողջարար & Դիետիկ",
                    detail: "Չի պարունակում շաքար, ներկանյութեր կամ ԳՄՕ: Խորհուրդ է տրվում դիաբետ ունեցողներին և քաշին հետևողներին:"
                },
                {
                    icon: <FireOutlined />,
                    label: "Տոսթերի Խորհուրդ 🔥",
                    detail: "Թեթևակի տապակեք տոսթերի կամ թավայի մեջ. հացը դառնում է անկրկնելի համեղ, խրթխրթան և էլ ավելի դյուրամարս:"
                }
            ],
            visitBtn: "Instagram (@tonus.bread)",
            moreBtn: "Իմանալ ավելին",
            locationsBtn: "Որտեղ գտնել 📍",
            locationsTitle: "«Տոնուս» Հացի Վաճառքի Հասցեները Հայաստանում",
            searchPlaceholder: "Փնտրել հասցե, փողոց կամ խանութ...",
            modalTitle: "«Տոնուս» Հացի Առավելությունները",
            modalItems: [
                { title: "Ծլեցված Հատիկներ", text: "Ծլեցման ընթացքում հացահատիկի մեջ վիտամինների և հանքանյութերի քանակը բազմապատկվում է:" },
                { title: "Առանց Ալյուրի", text: "Ալյուրի բացակայությունը թույլ է տալիս պահպանել հատիկի բնական բջջանյութը և սննդարար արժեքը:" },
                { title: "Մարսողության Աջակցություն", text: "Բարձր բջջանյութի շնորհիվ կարգավորում է նյութափոխանակությունը և մարսողությունը:" },
                { title: "Իդեալական Տոստերի Համար", text: "Թեթևակի տոստ անելուց հետո բացահայտվում է հացահատիկի բնական հարուստ համը:" }
            ]
        },
        ru: {
            title: "«ТОНУС» — Хлеб Без Муки и Дрожжей для Здорового Питания 🌾",
            subtitle: "Изготовлен из 100% пророщенных злаков в Армении. Сохраняет все природные витамины и минералы!",
            badges: ["0% Муки и Дрожжей", "100% Пророщенное Зерно", "Низкий ГИ"],
            tabs: [
                {
                    icon: <SafetyCertificateOutlined />,
                    label: "0% Муки и Дрожжей",
                    detail: "Изготавливается не из муки, а из цельных пророщенных зерен пшеницы по уникальной технологии."
                },
                {
                    icon: <HeartOutlined />,
                    label: "Полезно и Диетически",
                    detail: "Не содержит сахара, красителей и ГМО. Рекомендуется при диабете и контроле веса."
                },
                {
                    icon: <FireOutlined />,
                    label: "Совет для Тоста 🔥",
                    detail: "Слегка обжарьте в тостере или на сковороде — хлеб станет невероятно вкусным и хрустящим!"
                }
            ],
            visitBtn: "Instagram (@tonus.bread)",
            moreBtn: "Подробнее",
            locationsBtn: "Адреса магазинов 📍",
            locationsTitle: "Где Купить Хлеб «Тонус» в Армении",
            searchPlaceholder: "Поиск адреса, улицы или магазина...",
            modalTitle: "Преимущества Хлеба «Тонус»",
            modalItems: [
                { title: "Пророщенное Зерно", text: "При прорастании количество витаминов и микроэлементов в зерне увеличивается в разы." },
                { title: "Без Муки", text: "Отсутствие муки сохраняет природную клетчатку и всю питательную ценность зерна." },
                { title: "Легкое Пищеварение", text: "Высокое содержание клетчатки нормализует обмен веществ и пищеварение." },
                { title: "Идеален для Тостов", text: "Легкая обжарка раскрывает богатый природный вкус пророщенных зерен." }
            ]
        },
        en: {
            title: '"TONUS" — Flour-Free & Yeast-Free Healthy Sprouted Grain Bread 🌾',
            subtitle: 'Made from 100% sprouted whole grains in Armenia. Packed with essential vitamins & fiber!',
            badges: ["0% Flour & Yeast", "100% Sprouted Grain", "Low Glycemic Index"],
            tabs: [
                {
                    icon: <SafetyCertificateOutlined />,
                    label: "0% Flour & Yeast",
                    detail: "Made directly from sprouted whole wheat grains without conventional flour."
                },
                {
                    icon: <HeartOutlined />,
                    label: "Healthy & Natural",
                    detail: "No added sugar, colors, or GMOs. Excellent choice for wellness & weight management."
                },
                {
                    icon: <FireOutlined />,
                    label: "Toasting Tip 🔥",
                    detail: "Toast lightly in a toaster or pan for extraordinary crispiness and optimal digestion!"
                }
            ],
            visitBtn: "Instagram (@tonus.bread)",
            moreBtn: "Learn More",
            locationsBtn: "Store Locations 📍",
            locationsTitle: "Where to Buy Tonus Bread in Armenia",
            searchPlaceholder: "Search street, address, or store...",
            modalTitle: 'Benefits of "Tonus" Bread',
            modalItems: [
                { title: "Sprouted Whole Grains", text: "Sprouting unlocks maximum vitamins, minerals, and enzymes inside whole grains." },
                { title: "Flour-Free Recipe", text: "Skipping flour keeps 100% of natural bran fiber intact for optimal nutrient absorption." },
                { title: "Digestive Wellness", text: "High fiber content supports smooth digestion and balanced metabolism." },
                { title: "Best Served Toasted", text: "Lightly toasting brings out the nutty, rich flavor of sprouted grains." }
            ]
        }
    };

    const t = isRu ? content.ru : isEn ? content.en : content.hy;

    // Build flat list of all locations for the "all" filter
    const allLocations = [];
    Object.entries(TONUS_LOCATIONS).forEach(([key, group]) => {
        if (key !== 'all' && group.addresses) {
            group.addresses.forEach(addr => {
                allLocations.push({
                    chainKey: key,
                    chainName: isRu ? group.nameRu : isEn ? group.nameEn : group.name,
                    icon: group.icon,
                    badgeColor: group.badgeColor,
                    address: addr
                });
            });
        }
    });

    // Filter locations based on selected chain and search query
    const filteredLocations = allLocations.filter(item => {
        const matchesChain = selectedChain === 'all' || item.chainKey === selectedChain;
        const matchesSearch = searchQuery === '' || 
            item.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.chainName.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesChain && matchesSearch;
    });

    const chainCounts = {
        all: allLocations.length,
        zovq: TONUS_LOCATIONS.zovq.addresses.length,
        sas: TONUS_LOCATIONS.sas.addresses.length,
        mg: TONUS_LOCATIONS.mg.addresses.length,
        gurmenia: TONUS_LOCATIONS.gurmenia.addresses.length,
        regions: TONUS_LOCATIONS.regions.addresses.length
    };

    return (
        <>
            <Card
                style={{
                    marginBottom: 20,
                    borderRadius: 16,
                    backgroundImage: `linear-gradient(90deg, rgba(20, 42, 24, 0.94) 0%, rgba(27, 56, 32, 0.88) 55%, rgba(27, 56, 32, 0.72) 100%), url('/assets/tonus_banner_bg.jpg')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
                    color: 'white',
                    overflow: 'hidden',
                    position: 'relative'
                }}
                bodyStyle={{ padding: '22px' }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Header Section with Official Tonus Logo & Info */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flex: '1 1 320px' }}>
                            {/* Official Tonus Emblem Badge */}
                            <div style={{
                                width: 84,
                                height: 84,
                                borderRadius: '50%',
                                overflow: 'hidden',
                                flexShrink: 0,
                                border: '3px solid #ffd700',
                                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.4)',
                                background: '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 2
                            }}>
                                <img 
                                    src="/assets/tonus_logo.png" 
                                    alt="Tonus Logo" 
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            </div>

                            <div>
                                <Title level={4} style={{ color: '#ffffff', margin: 0, fontWeight: 800, letterSpacing: '0.2px' }}>
                                    {t.title}
                                </Title>
                                <Text style={{ color: 'rgba(255, 255, 255, 0.92)', fontSize: 13.5, display: 'block', marginTop: 4 }}>
                                    {t.subtitle}
                                </Text>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                                    {t.badges.map((b, i) => (
                                        <Tag 
                                            key={i} 
                                            style={{ 
                                                background: i === 0 ? 'rgba(255, 215, 0, 0.22)' : 'rgba(255, 255, 255, 0.15)', 
                                                color: i === 0 ? '#ffd700' : '#ffffff',
                                                border: '1px solid ' + (i === 0 ? 'rgba(255, 215, 0, 0.5)' : 'rgba(255, 255, 255, 0.25)'),
                                                borderRadius: 6,
                                                fontSize: 11.5,
                                                fontWeight: 700
                                            }}
                                        >
                                            {b}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <Space style={{ alignSelf: 'center' }} size="small" wrap>
                            <Button
                                type="default"
                                icon={<EnvironmentOutlined style={{ color: '#ffd700' }} />}
                                onClick={() => setIsLocationsOpen(true)}
                                style={{
                                    borderRadius: 10,
                                    height: 44,
                                    background: 'rgba(255, 215, 0, 0.18)',
                                    color: '#ffd700',
                                    borderColor: 'rgba(255, 215, 0, 0.5)',
                                    fontWeight: 700,
                                    fontSize: 13.5,
                                    backdropFilter: 'blur(4px)',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
                                }}
                            >
                                {t.locationsBtn}
                            </Button>
                            <Button
                                type="default"
                                icon={<InfoCircleOutlined />}
                                onClick={() => setIsModalOpen(true)}
                                style={{
                                    borderRadius: 10,
                                    height: 44,
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    color: '#ffffff',
                                    borderColor: 'rgba(255, 255, 255, 0.35)',
                                    fontWeight: 600,
                                    fontSize: 13.5,
                                    backdropFilter: 'blur(4px)'
                                }}
                            >
                                {t.moreBtn}
                            </Button>
                            <Button 
                                type="primary" 
                                size="large" 
                                href={INSTAGRAM_URL} 
                                target="_blank"
                                rel="noopener noreferrer"
                                icon={<InstagramOutlined />}
                                style={{ 
                                    borderRadius: 10, 
                                    fontWeight: 700, 
                                    height: 44,
                                    background: 'linear-gradient(135deg, #e1306c 0%, #fd1d1d 50%, #f56040 100%)',
                                    borderColor: '#e1306c',
                                    color: '#ffffff',
                                    boxShadow: '0 4px 14px rgba(225, 48, 108, 0.4)',
                                    fontSize: 14
                                }}
                            >
                                {t.visitBtn}
                            </Button>
                        </Space>
                    </div>

                    {/* Mobile-Friendly Non-Scrollable Interactive Feature Tabs (Grid / Wrap Layout) */}
                    <div style={{
                        background: 'rgba(0, 0, 0, 0.35)',
                        borderRadius: 12,
                        padding: 12,
                        backdropFilter: 'blur(6px)',
                        border: '1px solid rgba(255, 255, 255, 0.12)'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 8 
                        }}>
                            {t.tabs.map((tab, idx) => {
                                const isActive = activeTab === idx;
                                return (
                                    <button
                                        key={idx}
                                        onClick={() => setActiveTab(idx)}
                                        style={{
                                            flex: '1 1 180px',
                                            padding: '10px 14px',
                                            borderRadius: 8,
                                            border: 'none',
                                            cursor: 'pointer',
                                            background: isActive 
                                                ? 'linear-gradient(135deg, rgba(255,215,0,0.3) 0%, rgba(255,165,0,0.3) 100%)' 
                                                : 'rgba(255, 255, 255, 0.08)',
                                            color: isActive ? '#ffd700' : 'rgba(255, 255, 255, 0.85)',
                                            boxShadow: isActive ? '0 0 0 1.5px rgba(255,215,0,0.6)' : 'none',
                                            textAlign: 'left',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            fontWeight: isActive ? 700 : 500,
                                            fontSize: 13
                                        }}
                                    >
                                        <span style={{ fontSize: 16, flexShrink: 0 }}>{tab.icon}</span>
                                        <span style={{ wordBreak: 'normal', lineHeight: '1.2' }}>{tab.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <div style={{ 
                            marginTop: 10, 
                            padding: '10px 14px', 
                            background: 'rgba(255, 255, 255, 0.1)', 
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10
                        }}>
                            <ThunderboltOutlined style={{ color: '#ffd700', fontSize: 16, flexShrink: 0 }} />
                            <Text style={{ color: '#ffffff', fontSize: 13.5, margin: 0 }}>
                                {t.tabs[activeTab].detail}
                            </Text>
                        </div>
                    </div>
                </div>
            </Card>

            {/* High-End Store Locations Modal with All Visible Filter Chips */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #1b3820, #2e5a32)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffd700',
                            fontSize: 18,
                            boxShadow: '0 2px 8px rgba(27,56,32,0.3)'
                        }}>
                            <EnvironmentOutlined />
                        </div>
                        <div>
                            <span style={{ fontWeight: 800, fontSize: 17, color: '#1b3820', display: 'block' }}>
                                {t.locationsTitle}
                            </span>
                            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                                {isRu ? 'Выберите сеть или воспользуйтесь поиском' : isEn ? 'Select store chain or use search' : 'Ընտրեք ցանցը կամ օգտվեք որոնումից'}
                            </Text>
                        </div>
                    </div>
                }
                visible={isLocationsOpen}
                onCancel={() => setIsLocationsOpen(false)}
                footer={[
                    <Button 
                        key="close" 
                        onClick={() => setIsLocationsOpen(false)}
                        style={{ borderRadius: 8, fontWeight: 600, paddingLeft: 20, paddingRight: 20 }}
                    >
                        {isRu ? 'Закрыть' : isEn ? 'Close' : 'Փակել'}
                    </Button>
                ]}
                width={780}
                style={{ top: 30 }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 6 }}>
                    {/* Search Bar */}
                    <Input 
                        prefix={<SearchOutlined style={{ color: '#2e5a32', fontSize: 16 }} />}
                        placeholder={t.searchPlaceholder}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        allowClear
                        size="large"
                        style={{ 
                            borderRadius: 10, 
                            borderColor: '#c8dbc8',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                        }}
                    />

                    {/* All Visible Store Filter Chips (Full Grid Wrap Layout) */}
                    <div>
                        <Text strong style={{ fontSize: 12, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8, display: 'block' }}>
                            {isRu ? 'Ցանցեր' : isEn ? 'Store Chains' : 'Ցանցեր'}:
                        </Text>
                        <div style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 8,
                            alignItems: 'center' 
                        }}>
                            {/* All Chip */}
                            <button
                                onClick={() => setSelectedChain('all')}
                                style={{
                                    padding: '7px 14px',
                                    borderRadius: 20,
                                    border: selectedChain === 'all' ? '2px solid #1b3820' : '1px solid #d9d9d9',
                                    background: selectedChain === 'all' ? '#1b3820' : '#ffffff',
                                    color: selectedChain === 'all' ? '#ffd700' : '#444444',
                                    cursor: 'pointer',
                                    fontWeight: selectedChain === 'all' ? 700 : 500,
                                    fontSize: 13,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    transition: 'all 0.2s ease',
                                    boxShadow: selectedChain === 'all' ? '0 3px 10px rgba(27,56,32,0.25)' : 'none'
                                }}
                            >
                                <span>🌟</span>
                                <span>{isRu ? TONUS_LOCATIONS.all.nameRu : isEn ? TONUS_LOCATIONS.all.nameEn : TONUS_LOCATIONS.all.name}</span>
                                <Badge 
                                    count={chainCounts.all} 
                                    style={{ 
                                        backgroundColor: selectedChain === 'all' ? '#ffd700' : '#eee', 
                                        color: selectedChain === 'all' ? '#1b3820' : '#666',
                                        fontWeight: 700,
                                        boxShadow: 'none'
                                    }} 
                                />
                            </button>

                            {/* Individual Chain Chips */}
                            {['zovq', 'sas', 'mg', 'gurmenia', 'regions'].map((key) => {
                                const group = TONUS_LOCATIONS[key];
                                const isSelected = selectedChain === key;
                                const name = isRu ? group.nameRu : isEn ? group.nameEn : group.name;

                                return (
                                    <button
                                        key={key}
                                        onClick={() => setSelectedChain(key)}
                                        style={{
                                            padding: '7px 14px',
                                            borderRadius: 20,
                                            border: isSelected ? `2px solid ${group.badgeColor}` : '1px solid #d9d9d9',
                                            background: isSelected ? group.badgeColor : '#ffffff',
                                            color: isSelected ? '#ffffff' : '#444444',
                                            cursor: 'pointer',
                                            fontWeight: isSelected ? 700 : 500,
                                            fontSize: 13,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            transition: 'all 0.2s ease',
                                            boxShadow: isSelected ? `0 3px 10px ${group.badgeColor}40` : 'none'
                                        }}
                                    >
                                        <span>{group.icon}</span>
                                        <span>{name}</span>
                                        <Badge 
                                            count={chainCounts[key]} 
                                            style={{ 
                                                backgroundColor: isSelected ? 'rgba(255,255,255,0.3)' : '#f0f0f0', 
                                                color: isSelected ? '#ffffff' : '#666',
                                                fontWeight: 700,
                                                boxShadow: 'none'
                                            }} 
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Address Cards Grid */}
                    <div style={{ 
                        marginTop: 4,
                        maxHeight: '380px', 
                        overflowY: 'auto', 
                        paddingRight: 4
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 10,
                            paddingBottom: 4,
                            borderBottom: '1px solid #f0f0f0'
                        }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {isRu ? `Найдено адресов: ${filteredLocations.length}` : isEn ? `Addresses found: ${filteredLocations.length}` : `Գտնվել է ${filteredLocations.length} հասցե`}
                            </Text>
                        </div>

                        {filteredLocations.length > 0 ? (
                            <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', 
                                gap: 10
                            }}>
                                {filteredLocations.map((item, index) => (
                                    <div 
                                        key={index} 
                                        style={{ 
                                            padding: '11px 13px', 
                                            background: '#f8fbf8', 
                                            borderRadius: 10, 
                                            border: '1px solid #e2ece2',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            gap: 6,
                                            transition: 'transform 0.15s ease, boxShadow 0.15s ease',
                                            cursor: 'default'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                            <Tag 
                                                style={{ 
                                                    background: item.badgeColor ? `${item.badgeColor}15` : '#e6f4ea',
                                                    color: item.badgeColor || '#1b3820',
                                                    border: `1px solid ${item.badgeColor || '#1b3820'}30`,
                                                    borderRadius: 6,
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    margin: 0,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 4
                                                }}
                                            >
                                                <span>{item.icon}</span>
                                                <span>{item.chainName}</span>
                                            </Tag>
                                            <ShopOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 2 }}>
                                            <EnvironmentOutlined style={{ color: '#2e5a32', fontSize: 15, marginTop: 3, flexShrink: 0 }} />
                                            <Text strong style={{ color: '#1b3820', fontSize: 13, lineHeight: '1.35' }}>
                                                {item.address}
                                            </Text>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '30px 10px' }}>
                                <EnvironmentOutlined style={{ fontSize: 36, color: '#d9d9d9', marginBottom: 8 }} />
                                <Text type="secondary" style={{ display: 'block', fontSize: 14 }}>
                                    {isRu ? 'По вашему запросу адреса не найдены' : isEn ? 'No matching store addresses found' : 'Համապատասխան հասցե չի գտնվել'}
                                </Text>
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Interactive Info Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <img 
                            src="/assets/tonus_logo.png" 
                            alt="Tonus Logo" 
                            style={{ width: 28, height: 28, objectFit: 'contain' }}
                        />
                        <span style={{ fontWeight: 700 }}>{t.modalTitle}</span>
                    </div>
                }
                visible={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={[
                    <Button 
                        key="close" 
                        onClick={() => setIsModalOpen(false)}
                        style={{ borderRadius: 8 }}
                    >
                        {isRu ? 'Закрыть' : isEn ? 'Close' : 'Փակել'}
                    </Button>,
                    <Button 
                        key="visit" 
                        type="primary"
                        href={INSTAGRAM_URL} 
                        target="_blank"
                        rel="noopener noreferrer"
                        icon={<InstagramOutlined />}
                        style={{ 
                            borderRadius: 8, 
                            background: 'linear-gradient(135deg, #e1306c 0%, #fd1d1d 50%, #f56040 100%)',
                            borderColor: '#e1306c'
                        }}
                    >
                        {t.visitBtn}
                    </Button>
                ]}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 10 }}>
                    {t.modalItems.map((item, index) => (
                        <div 
                            key={index}
                            style={{ 
                                display: 'flex', 
                                gap: 12, 
                                padding: 12, 
                                background: '#f8f9fa', 
                                borderRadius: 10,
                                borderLeft: '4px solid #1b3820'
                            }}
                        >
                            <CheckCircleOutlined style={{ color: '#1b3820', fontSize: 18, marginTop: 2 }} />
                            <div>
                                <Text strong style={{ color: '#1b3820', display: 'block', fontSize: 14 }}>
                                    {item.title}
                                </Text>
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                    {item.text}
                                </Text>
                            </div>
                        </div>
                    ))}
                </div>
            </Modal>
        </>
    );
};

export default TonusBanner;
